import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, MailCheck } from "lucide-react";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { Button } from "../components/ui/button";
import { IMMIGRATION_SERVICES, COURT_MATTERS, IMAGES } from "../data/content";

const CORRESPONDENCE_SERVICES = [
  "Filing the appropriate notice of representation",
  "USCIS correspondence management",
  "Immigration court correspondence where authorized",
  "Receipt and tracking of eligible notices",
  "RFE tracking", "NOID tracking", "Interview notices", "Biometrics notices",
  "Hearing dates", "Filing deadlines", "Appeal deadlines",
  "Address-change assistance", "Case-status tracking",
  "Secure document storage", "Copies of submissions", "Copies of government notices",
  "Client reminders", "Response coordination",
];

export default function Immigration() {
  return (
    <div data-testid="immigration-page">
      <PageHero
        eyebrow="Immigration"
        title="Immigration Legal Services"
        lead="Immigration matters can affect your family, your ability to work, your future, and your ability to remain in the United States. Our immigration legal-services program helps eligible clients understand their options, prepare accurate filings, organize evidence, respond to government requests, and navigate their case within the scope of our authorized representation."
        image={IMAGES.stamps}
        alt="Passport stamps representing international journeys"
      />

      <section className="py-16 lg:py-24" data-testid="immigration-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight mb-10">How we can help</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {IMMIGRATION_SERVICES.map((s, i) => (
              <div key={s.title} className="bg-surface border border-line rounded-xl p-6 shadow-sm hover:border-teal hover:shadow-md transition-all" data-testid={`immigration-service-${i}`}>
                <h3 className="text-base font-sans font-semibold text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-mutedink leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURT & REMOVAL DEFENSE */}
      <section className="py-16 lg:py-20 bg-[#101315]" data-testid="court-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-champagne mb-3">Immigration Court &amp; Removal Defense</p>
              <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-cream tracking-tight">Facing court is frightening. You should not face it alone.</h2>
              <div className="mt-6 bg-surface/5 border border-white/15 rounded-xl p-5 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-champagne flex-shrink-0 mt-0.5" />
                <p className="text-sm text-cream/80 leading-relaxed">
                  Representation before immigration courts and the Board of Immigration Appeals is offered only when provided by a fully accredited representative or authorized attorney and within the organization's approved scope.
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-sans font-semibold text-cream mb-4">Potential matters can include</h3>
              <ul className="space-y-3">
                {COURT_MATTERS.map((m) => (
                  <li key={m} className="flex items-start gap-3 text-sm text-cream/85">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-champagne flex-shrink-0" /> {m}
                  </li>
                ))}
              </ul>
              <Button asChild className="mt-8 bg-champagne text-espresso hover:bg-champagne-dark hover:text-espresso rounded-full" data-testid="court-consultation-button">
                <Link to="/start">Request a Confidential Review <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* REPRESENTATIVE OF RECORD */}
      <section className="py-16 lg:py-24 bg-sand/60" data-testid="correspondence-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal mb-3">Representative of Record &amp; Correspondence</p>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">Your Case. Your Correspondence. One Organized File.</h2>
            <p className="mt-4 text-lg text-mutedink leading-relaxed">
              When permitted by the applicable agency and representation rules, CF Full Circle can serve as the client's authorized immigration representative and help manage official case correspondence.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CORRESPONDENCE_SERVICES.map((s) => (
              <div key={s} className="flex items-start gap-3 bg-surface border border-line rounded-lg p-4">
                <MailCheck className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                <span className="text-sm text-ink">{s}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-mutedink italic max-w-3xl" data-testid="correspondence-disclaimer">
            Not every government document can be redirected to a representative. Delivery depends on the agency, case type, form, and client's elections.
          </p>
        </div>
      </section>

      <CTASection title="Not sure which immigration path fits your situation?" text="Start with a guided intake. We will help you understand your options." />
    </div>
  );
}
